import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMailFoldersQueued } from "@/lib/api/folderFetchCoordinator";
import { MAIL_LIVE_SYNC_MS } from "@/lib/constants/inbox";
import {
  clearFoldersSessionCache,
  readFoldersSessionCache,
  writeFoldersSessionCache,
} from "@/lib/cache/foldersSessionCache";
import type { MailFolder } from "@/lib/types/mail";
import { normalizeMailFolderLabels } from "@/lib/folders/folderLabels";
import { folderUnreadAfterDeleteHold } from "@/lib/mail/mailMutationHold";
import { syncInboxHeadersIfBadgeAhead } from "@/lib/mail/syncInboxIfBadgeAhead";
import { ensureAureliaFolderOverrides } from "@/lib/mail/sentFolderSetup";

const FALLBACK_FOLDERS: MailFolder[] = [
  { name: "INBOX", label: "Boîte de réception", depth: 0, unread: 0 },
];

const FOLDER_FETCH_RETRIES = 2;
const FOLDER_FETCH_RETRY_MS = 2_500;

function isImapConnectionError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes("too many simultaneous") ||
    m.includes("simultaneous connection") ||
    m.includes("authenticate to imap")
  );
}

export function useMailFolders(
  accountId?: number,
  options?: { skipImap?: boolean }
) {
  const skipImap = options?.skipImap === true;
  const sessionSeed =
    accountId != null ? readFoldersSessionCache(accountId) : null;

  const [folders, setFolders] = useState<MailFolder[]>(() =>
    sessionSeed ? normalizeMailFolderLabels(sessionSeed) : FALLBACK_FOLDERS
  );
  const [loading, setLoading] = useState(
    () => !sessionSeed || sessionSeed.length <= 1
  );
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);

  const foldersRef = useRef(folders);
  foldersRef.current = folders;

  const reload = useCallback(
    async (refresh = false): Promise<boolean> => {
      if (!accountId || skipImap) {
        setFolders(FALLBACK_FOLDERS);
        setLoading(false);
        setSyncing(false);
        if (skipImap) setError(null);
        return true;
      }

      if (refresh) {
        clearFoldersSessionCache(accountId);
      }

      const hasVisible = foldersRef.current.length > 1;
      if (hasVisible) {
        setSyncing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        let lastMessage = "";
        for (let attempt = 0; attempt <= FOLDER_FETCH_RETRIES; attempt += 1) {
          if (attempt > 0) {
            await new Promise((r) => window.setTimeout(r, FOLDER_FETCH_RETRY_MS * attempt));
          }

          const res = await fetchMailFoldersQueued({ accountId, refresh });

          if (res.ok) {
            const list = normalizeMailFolderLabels(
              res.data.folders?.length ? res.data.folders : FALLBACK_FOLDERS
            );
            ensureAureliaFolderOverrides(accountId, list);
            setFolders(list);
            setFromCache(res.data.cache === "hit" || res.data.cache === "hit-aurelia-ensured");
            writeFoldersSessionCache(accountId, list);
            return true;
          }

          lastMessage = res.message;
          if (!isImapConnectionError(res.message) || attempt >= FOLDER_FETCH_RETRIES) {
            break;
          }
        }

        setError(lastMessage || "Impossible de charger les dossiers IMAP.");
        return false;
      } finally {
        if (hasVisible) {
          setSyncing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [accountId, skipImap]
  );

  const adjustFolderUnread = useCallback(
    (folderName: string, delta: number) => {
      if (!accountId || delta === 0) return;

      setFolders((prev) => {
        const next = prev.map((f) =>
          f.name === folderName
            ? { ...f, unread: Math.max(0, (f.unread ?? 0) + delta) }
            : f
        );
        writeFoldersSessionCache(accountId, next);
        return next;
      });
    },
    [accountId]
  );

  const refreshBadges = useCallback(async () => {
    if (!accountId || skipImap) return;

    const res = await fetchMailFoldersQueued({ accountId, badges: true });
    if (!res.ok) return;

    const list = normalizeMailFolderLabels(
      res.data.folders?.length ? res.data.folders : FALLBACK_FOLDERS
    );
    const reconciled = list.map((folder) => {
      const local = foldersRef.current.find((item) => item.name === folder.name);
      const unread = folderUnreadAfterDeleteHold(
        accountId,
        folder.name,
        folder.unread ?? 0,
        local?.unread
      );
      return unread === (folder.unread ?? 0) ? folder : { ...folder, unread };
    });
    setFolders(reconciled);
    setFromCache(res.data.cache === "hit");
    writeFoldersSessionCache(accountId, reconciled);
    await syncInboxHeadersIfBadgeAhead(accountId, reconciled);
  }, [accountId, skipImap]);

  useEffect(() => {
    if (!accountId) {
      setFolders(FALLBACK_FOLDERS);
      setLoading(false);
      setSyncing(false);
      setError(null);
      setFromCache(false);
      return;
    }
    // Re-seed immédiat au switch (sinon dossiers/unread du compte précédent).
    const seed = readFoldersSessionCache(accountId);
    setFolders(seed?.length ? normalizeMailFolderLabels(seed) : FALLBACK_FOLDERS);
    setLoading(!seed || seed.length <= 1);
    setSyncing(false);
    setError(null);
    setFromCache(Boolean(seed?.length));
    void reload(false);
  }, [accountId, reload, skipImap]);

  useEffect(() => {
    if (!accountId || skipImap) return;

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void refreshBadges();
    };

    const intervalId = window.setInterval(tick, MAIL_LIVE_SYNC_MS);
    return () => window.clearInterval(intervalId);
  }, [accountId, refreshBadges, skipImap]);

  return { folders, loading, syncing, error, fromCache, reload, refreshBadges, adjustFolderUnread };
}
